import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import { Shield, Eye, EyeOff, Copy, Check } from 'lucide-react';

interface RecoveryPhraseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Space-separated BIP-39 mnemonic. */
  mnemonic: string;
}

/**
 * An elegant, security-conscious recovery-phrase reveal — the wallet's single
 * most sensitive surface, so it deserves better than a raw `alert()`.
 *
 * Principles:
 *  • Hidden by default (blurred word grid) so the seed can't be shoulder-surfed
 *    just by opening the dialog; the user must deliberately tap "Reveal".
 *  • Numbered word chips make it easy to transcribe accurately onto paper.
 *  • Copy is available but explicitly discouraged in the warning (clipboards
 *    leak); paper is the safe path.
 *  • The seed is cleared from the reveal state whenever the dialog closes.
 */
export function RecoveryPhraseDialog({ open, onOpenChange, mnemonic }: RecoveryPhraseDialogProps) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const words = mnemonic.trim().split(/\s+/);

  const handleOpenChange = (next: boolean) => {
    // Re-hide on every close so re-opening always starts concealed.
    if (!next) {
      setRevealed(false);
      setCopied(false);
    }
    onOpenChange(next);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(mnemonic);
      setCopied(true);
      toast({ title: 'Copied', description: 'Recovery phrase copied — paste it somewhere safe, then clear your clipboard.' });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast({ variant: 'destructive', title: 'Copy failed', description: 'Please write the words down instead.' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[calc(100%-1rem)] sm:max-w-md p-5 sm:p-6">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/15">
            <Shield className="h-6 w-6 text-amber-500" />
          </div>
          <DialogTitle className="text-center text-lg font-black">Recovery Phrase</DialogTitle>
          <DialogDescription className="text-center text-xs leading-relaxed">
            These 12 words are the only way to restore your ecash balance. Write them down in order
            and store them offline — never share them with anyone.
          </DialogDescription>
        </DialogHeader>

        {/* Word grid */}
        <div className="relative">
          <div
            className={cn(
              'grid grid-cols-2 gap-2 rounded-2xl border border-amber-500/20 bg-amber-500/[0.04] p-3 transition-all sm:grid-cols-3',
              !revealed && 'select-none blur-sm',
            )}
            aria-hidden={!revealed}
          >
            {words.map((word, i) => (
              <div
                key={`${i}-${word}`}
                className="flex items-center gap-2 rounded-lg bg-background/60 px-2.5 py-2"
              >
                <span className="w-4 text-right text-[10px] font-bold tabular-nums text-muted-foreground">
                  {i + 1}
                </span>
                <span className="font-mono text-sm font-semibold">{word}</span>
              </div>
            ))}
          </div>

          {/* Reveal overlay */}
          {!revealed && (
            <button
              type="button"
              onClick={() => setRevealed(true)}
              className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 rounded-2xl bg-background/40 backdrop-blur-[2px] transition-colors hover:bg-background/30 active:scale-[0.99]"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/20">
                <Eye className="h-5 w-5 text-amber-500" />
              </span>
              <span className="text-xs font-bold uppercase tracking-wide text-amber-400">
                Tap to reveal
              </span>
              <span className="text-[10px] text-muted-foreground">Make sure nobody's watching</span>
            </button>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1 gap-2"
            onClick={() => setRevealed((v) => !v)}
          >
            {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {revealed ? 'Hide' : 'Reveal'}
          </Button>
          <Button
            variant="outline"
            className="flex-1 gap-2"
            onClick={handleCopy}
            disabled={!revealed}
          >
            {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>

        <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
          Writing it on paper is safest — copying to your clipboard can leak to other apps.
        </p>
      </DialogContent>
    </Dialog>
  );
}
